import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import Grid from "@mui/material/Unstable_Grid2";
import { Paper, Typography, Backdrop, CircularProgress } from "@mui/material";
import { DataTable } from "../../common/dataTable/DataTable";
import { ProductListingFilters } from "../../common/productListingFilters/ProductListingFilters";
import { initLocalization } from "../../../utils/helpers";
import { productListingStrings } from "./locale";
import {
  DEFAULT_SELF_PROMOTION_PRODUCT_LISTING_FILTERS,
  MAPPING_KEYS_DATE_FORMAT,
} from "../../../constants";

import useProductListing from "../../../hooks/common/useProductListing";

export const ProductListing = ({
  activationFilterOption,
  onSelectedProducts,
  isLoading = false,
}) => {
  initLocalization(productListingStrings);

  const lastSelected = useRef(null);
  const [filters, setFilters] = useState(
    DEFAULT_SELF_PROMOTION_PRODUCT_LISTING_FILTERS,
  );
  const [selectedRows, setSelectedRows] = useState(() => new Set());

  const {
    isLoading: productLoading,
    productsData,
    setProductsData,
    getProductsData,
  } = useProductListing(activationFilterOption);

  const columns = [
    {
      field: "productId",
      headerName: productListingStrings.productID,
      minWidth: 65,
    },
    {
      field: "productLabel",
      headerName: productListingStrings.productLabel,
      minWidth: 190,
    },
    {
      field: "advertiserLabel",
      headerName: productListingStrings.advertiserLabel,
      minWidth: 190,
    },
    {
      field: "activationId",
      headerName: productListingStrings.activationID,
      minWidth: 40,
    },
    {
      field: "creationDate",
      headerName: productListingStrings.creationDate,
      minWidth: 82,
      format: (value) => dayjs(value).format(MAPPING_KEYS_DATE_FORMAT),
    },
    {
      field: "groupLabel",
      headerName: productListingStrings.groupLabel,
      minWidth: 190,
    },
  ];

  const resetProductsTable = () => {
    setProductsData([]);
    setSelectedRows(() => new Set());
    onSelectedProducts([]);
  };

  const getSelected = (currentIndex, allowMultiple, indices) => {
    const prevArray = [...selectedRows];
    const current = prevArray.find(
      (id) => id === productsData[currentIndex].id,
    );
    const currentSet =
      current && prevArray.length === 1 ? new Set([current]) : new Set();
    const next = allowMultiple ? new Set(selectedRows) : currentSet;

    indices.forEach((idx) => {
      const id = productsData[idx].id;
      next.has(id) ? next.delete(id) : next.add(id);
    });

    return next;
  };

  const handleSelectRow = (event, index) => {
    const indices = new Set();
    let allowMultiple = false;

    indices.add(index);

    if (event.shiftKey) {
      const limits = [lastSelected.current ?? 0, index].sort((a, b) => a - b);
      const [start, end] = limits;

      for (let i = start; i <= end; i++) {
        indices.add(i);
      }

      indices.delete(lastSelected.current);
      allowMultiple = true;
    } else if (event.metaKey || event.ctrlKey) {
      allowMultiple = true;
    }

    const newSelection = getSelected(index, allowMultiple, indices);

    setSelectedRows(newSelection);
    onSelectedProducts(
      [...productsData].filter((entry) => newSelection.has(entry.id)),
    );

    lastSelected.current = index;
  };

  return (
    <Paper sx={{ padding: "0.5rem", height: "100%", position: "relative" }}>
      <Grid container direction="column" height="100%">
        <Grid xs={12}>
          <Typography data-testid="product-listing-title">
            {productListingStrings.title}
          </Typography>
        </Grid>

        <Grid xs={12} marginTop={{ xs: 2, sm: 0 }}>
          <ProductListingFilters
            allowSearch={true}
            filters={filters}
            setFilters={setFilters}
            resetProductsTable={resetProductsTable}
            getProductsData={getProductsData}
            showSocialAccountsCheckbox={false}
          />
        </Grid>

        <Grid xs={12} display="flex" flexDirection="column" flexGrow="1">
          <DataTable
            selectedRows={[...selectedRows]}
            columns={columns}
            rows={productsData}
            showPagination={false}
            allowSelection={true}
            showNoDataMessage={false}
            onSelectRow={handleSelectRow}
            tableMinWidth={762}
          />
        </Grid>
      </Grid>

      <Backdrop
        open={productLoading || isLoading}
        sx={{
          position: "absolute",
          color: "#fff",
          borderRadius: 1,
          zIndex: (theme) => theme.zIndex.modal + 1,
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Paper>
  );
};

ProductListing.propTypes = {
  activationFilterOption: PropTypes.string.isRequired,
  onSelectedProducts: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};
